import { Injectable, computed, signal } from '@angular/core';
import type { QuantityBreak } from '../domain/product-quantity';
import {
  CART_STORAGE_KEY,
  cartBlockTotalCents,
  cartItemCount,
  cartLineKey,
  cartLineTotalCents,
  cartLineUnitCents,
  cartProducerCount,
  cartTotalCents,
  readStoredCart,
  type CartLine,
  type CartProducerBlock,
  type CartState,
} from './cart';

export interface AddToCartInput {
  readonly producerId: string;
  readonly producerName: string;
  readonly commune: string;
  readonly cardAccepted: boolean;
  readonly productId: string;
  readonly productName: string;
  readonly quantity: number;
  readonly priceType: string;
  readonly unit: string;
  readonly unitPriceCents: number;
  readonly antiWastePercent?: number;
  readonly extraIds: readonly string[];
  readonly extrasLabel?: string | null;
  readonly extraCents?: number;
  readonly baseUnitPriceCents?: number;
  readonly quantityStep?: number;
  readonly minQuantity?: number;
  readonly quantityBreaks?: readonly QuantityBreak[];
}

/**
 * Panier multi-producteur, persisté localement.
 */
@Injectable({ providedIn: 'root' })
export class CartService {
  private readonly state = signal<CartState | null>(readStoredCart());

  readonly cart = this.state.asReadonly();
  readonly hasItems = computed(() => cartItemCount(this.state()) > 0);
  readonly itemCount = computed(() => cartItemCount(this.state()));
  readonly totalCents = computed(() => cartTotalCents(this.state()));
  readonly producerCount = computed(() => cartProducerCount(this.state()));
  readonly producerNames = computed(() =>
    (this.state()?.blocks ?? []).map((block) => block.producerName),
  );
  readonly isMultiProducer = computed(() => this.producerCount() > 1);

  /** Premier producteur — compatibilité barre panier mono. */
  readonly producerName = computed(() => this.state()?.blocks[0]?.producerName ?? '');

  replaceState(state: CartState): void {
    this.persist(state, true);
  }

  onPersist(listener: (state: CartState | null) => void): () => void {
    this.persistListeners.add(listener);
    return () => {
      this.persistListeners.delete(listener);
    };
  }

  private readonly persistListeners = new Set<(state: CartState | null) => void>();

  add(input: AddToCartInput): 'added' | 'merged' {
    const lineKey = cartLineKey(input.productId, input.extraIds);
    const line: CartLine = {
      lineKey,
      productId: input.productId,
      productName: input.productName,
      quantity: input.quantity,
      priceType: input.priceType,
      unit: input.unit,
      unitPriceCents: input.unitPriceCents,
      antiWastePercent: input.antiWastePercent,
      extraIds: [...input.extraIds],
      extrasLabel: input.extrasLabel ?? null,
      extraCents: input.extraCents,
      baseUnitPriceCents: input.baseUnitPriceCents,
      quantityStep: input.quantityStep,
      minQuantity: input.minQuantity,
      quantityBreaks: input.quantityBreaks,
    };

    const current = this.state();
    const blockIndex = current?.blocks.findIndex((block) => block.producerId === input.producerId) ?? -1;

    if (current === null || blockIndex < 0) {
      const block: CartProducerBlock = {
        producerId: input.producerId,
        producerName: input.producerName,
        commune: input.commune,
        cardAccepted: input.cardAccepted,
        lines: [line],
      };
      this.persist({
        version: 2,
        blocks: [...(current?.blocks ?? []), block],
        updatedAt: new Date().toISOString(),
      });
      return 'added';
    }

    const block = current.blocks[blockIndex];
    if (block === undefined) {
      return 'added';
    }
    const existing = block.lines.find((entry) => entry.lineKey === lineKey);
    if (existing !== undefined) {
      const merged: CartLine = {
        ...line,
        quantity: existing.quantity + input.quantity,
      };
      const nextBlock: CartProducerBlock = {
        ...block,
        producerName: input.producerName,
        commune: input.commune,
        cardAccepted: input.cardAccepted,
        lines: block.lines
          .filter((entry) => entry.lineKey !== lineKey)
          .concat({ ...merged, unitPriceCents: cartLineUnitCents(merged) }),
      };
      const blocks = [...current.blocks];
      blocks[blockIndex] = nextBlock;
      this.persist({ ...current, blocks, updatedAt: new Date().toISOString() });
      return 'merged';
    }

    const nextBlock: CartProducerBlock = {
      ...block,
      producerName: input.producerName,
      commune: input.commune,
      cardAccepted: input.cardAccepted,
      lines: [...block.lines, line],
    };
    const blocks = [...current.blocks];
    blocks[blockIndex] = nextBlock;
    this.persist({ ...current, blocks, updatedAt: new Date().toISOString() });
    return 'added';
  }

  updateQuantity(producerId: string, lineKey: string, quantity: number): void {
    const current = this.state();
    if (current === null || quantity <= 0) {
      return;
    }
    const blocks = current.blocks.map((block) => {
      if (block.producerId !== producerId) {
        return block;
      }
      return {
        ...block,
        lines: block.lines.map((line) => {
          if (line.lineKey !== lineKey) {
            return line;
          }
          const next: CartLine = { ...line, quantity };
          return { ...next, unitPriceCents: cartLineUnitCents(next) };
        }),
      };
    });
    this.persist({ ...current, blocks, updatedAt: new Date().toISOString() });
  }

  remove(producerId: string, lineKey: string): void {
    const current = this.state();
    if (current === null) {
      return;
    }
    const blocks = current.blocks
      .map((block) => {
        if (block.producerId !== producerId) {
          return block;
        }
        const lines = block.lines.filter((line) => line.lineKey !== lineKey);
        if (lines.length === 0) {
          return null;
        }
        return { ...block, lines };
      })
      .filter((block): block is CartProducerBlock => block !== null);
    if (blocks.length === 0) {
      this.clear();
      return;
    }
    this.persist({ ...current, blocks, updatedAt: new Date().toISOString() });
  }

  clear(): void {
    this.state.set(null);
    try {
      localStorage.removeItem(CART_STORAGE_KEY);
    } catch {
      // Mode privé ou stockage indisponible.
    }
    this.notifyPersist(null);
  }

  lineTotal(line: CartLine): number {
    return cartLineTotalCents(line);
  }

  blockTotal(block: CartProducerBlock): number {
    return cartBlockTotalCents(block);
  }

  private persist(next: CartState, suppressSync = false): void {
    this.state.set(next);
    try {
      localStorage.setItem(CART_STORAGE_KEY, JSON.stringify(next));
    } catch {
      // Panier en mémoire seulement.
    }
    if (!suppressSync) {
      this.notifyPersist(next);
    }
  }

  private notifyPersist(state: CartState | null): void {
    for (const listener of this.persistListeners) {
      listener(state);
    }
  }
}
